/**
 * Esqueletos de carga.
 *
 * Cada pantalla muestra la SILUETA de su contenido mientras carga, no un spinner centrado: así
 * el layout no salta al llegar los datos. Nunca hay pantalla en blanco.
 */
import { useEmbedded } from './EmbeddedContext';
import styles from './Skeleton.module.css';

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  radius?: number;
  className?: string;
}

export function Skeleton({ width = '100%', height = 14, radius = 8, className }: SkeletonProps) {
  return (
    <div
      className={[styles.block, className].filter(Boolean).join(' ')}
      style={{ width, height, borderRadius: radius }}
    />
  );
}

/** Fila de N tarjetas de KPI. */
export function SkeletonKpiRow({ count = 4 }: { count?: number }) {
  return (
    <div className={count === 3 ? styles.grid3 : styles.grid4}>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className={styles.kpiCard}>
          <Skeleton width="60%" height={12} />
          <div style={{ height: 12 }} />
          <Skeleton width="80%" height={26} />
        </div>
      ))}
    </div>
  );
}

/** Tarjeta con una cabecera y N filas. */
export function SkeletonCard({ rows = 4, height = 44 }: { rows?: number; height?: number }) {
  return (
    <div className={styles.card}>
      <Skeleton width="30%" height={16} className={styles.headerBlock} />
      <div className={styles.rows}>
        {Array.from({ length: rows }, (_, index) => (
          <Skeleton key={index} height={height} radius={6} />
        ))}
      </div>
    </div>
  );
}

/** Cabecera de pantalla: título + subtítulo. */
export function SkeletonPageHeader() {
  // Dentro de una pestaña el encabezado es una sola línea: el esqueleto también.
  if (useEmbedded()) {
    return (
      <div className={styles.headerBlock}>
        <Skeleton width={380} height={15} />
      </div>
    );
  }
  return (
    <div className={styles.headerBlock}>
      <Skeleton width={320} height={34} radius={6} />
      <div style={{ height: 10 }} />
      <Skeleton width={420} height={15} />
    </div>
  );
}

/** Rejilla de tarjetas (inquilinos, informes). */
export function SkeletonGrid({ count = 6, height = 210 }: { count?: number; height?: number }) {
  return (
    <div className={styles.grid3}>
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} height={height} radius={8} />
      ))}
    </div>
  );
}
